/**
 * Shared Organization (NGO) JSON-LD schema for Google Knowledge Panel readiness.
 * Used across all public pages via the SEO component.
 */

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["NGO", "Organization"],
  "@id": "https://abbashouse.org/#organization",
  name: "Abba's House Communities Inc.",
  alternateName: [
    "Abba's House Communities",
    "Abbas House Communities",
    "Abbas House Communities Inc",
  ],
  url: "https://abbashouse.org",
  logo: "https://images.abbashouse.org/assets/logo-header.png",
  image: "https://images.abbashouse.org/assets/facility-1.webp",
  description:
    "A nonprofit Christian ministry building Community Housing Centers to end homelessness through free 90-day transition programs, job placement, healthcare assistance, and dignified community living.",
  slogan: "A Home For Everyone",
  foundingDate: "2026-02-18",
  founder: {
    "@type": "Person",
    name: "Dalton McGhee",
  },
  taxID: "41-4644298",
  nonprofitStatus: "Nonprofit501c3",
  telephone: "+1-833-707-4673",
  email: "hello@abbashouse.org",
  address: {
    "@type": "PostalAddress",
    streetAddress: "101 N 1st Ave, Suite 2325",
    addressLocality: "Phoenix",
    addressRegion: "AZ",
    postalCode: "85003",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-833-707-4673",
    contactType: "customer service",
    email: "hello@abbashouse.org",
    availableLanguage: "English",
  },
  sameAs: [
    "https://www.facebook.com/ourabbashouse",
    "https://www.instagram.com/ourabbashouse",
    "https://app.candid.org/profile/16557069/abbas-house-communities-inc-41-4644298",
  ],
  knowsAbout: [
    "Homelessness",
    "Community Housing",
    "Nonprofit Housing",
    "Transitional Housing",
    "Job Placement",
    "Healthcare Assistance",
  ],
  potentialAction: {
    "@type": "DonateAction",
    name: "Donate to Abba's House Communities",
    target: "https://give.abbashouse.org",
    recipient: {
      "@id": "https://abbashouse.org/#organization",
    },
  },
};

/**
 * Returns a WebPage JSON-LD object linked to the organization.
 */
export function webPageSchema(opts: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    isPartOf: {
      "@type": "WebSite",
      "@id": "https://abbashouse.org/#website",
      name: "Abba's House Communities",
      url: "https://abbashouse.org",
    },
    publisher: {
      "@id": "https://abbashouse.org/#organization",
    },
  };
}

/**
 * Returns a BreadcrumbList JSON-LD object for interior pages.
 * Pass the trail starting from Home, e.g.
 *   breadcrumbSchema([{ name: "Home", url: "https://abbashouse.org/" }, { name: "About", url: "https://abbashouse.org/about" }])
 */
export function breadcrumbSchema(
  items: { name: string; url: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Returns a DonateAction JSON-LD object for donation-focused pages.
 */
export function donateActionSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    name: "Donate to Abba's House Communities",
    description:
      "Support Abba's House Communities — a 501(c)(3) nonprofit building Community Housing Centers to end homelessness. Donations are tax deductible.",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://give.abbashouse.org",
    },
    recipient: {
      "@id": "https://abbashouse.org/#organization",
    },
  };
}

/**
 * Returns an Event JSON-LD object for community events.
 */
export function eventSchema(opts: {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  url: string;
  locationName?: string;
  locationAddress?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: opts.name,
    description: opts.description,
    startDate: opts.startDate,
    endDate: opts.endDate,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    url: opts.url,
    image: opts.image ?? "https://images.abbashouse.org/assets/og-image.png",
    location: opts.locationName
      ? {
          "@type": "Place",
          name: opts.locationName,
          address: opts.locationAddress,
        }
      : undefined,
    organizer: {
      "@id": "https://abbashouse.org/#organization",
    },
  };
}

/**
 * Returns an Article JSON-LD object for blog posts / news articles.
 */
export function articleSchema(opts: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    url: opts.url,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: {
      "@type": opts.authorName ? "Person" : "Organization",
      name: opts.authorName ?? "Abba's House Communities",
    },
    publisher: {
      "@id": "https://abbashouse.org/#organization",
    },
    image: opts.image ?? "https://images.abbashouse.org/assets/og-image.png",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": opts.url,
    },
  };
}
